import React, { useState } from "react";
import {
  Box, Card, CardContent, Typography, Chip, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, CircularProgress,
  InputAdornment, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, Avatar, Button,
} from "@mui/material";
import { Search, Eye, Inbox, X, Mail, Phone, Building2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { adminGetInquiries, adminGetInquiry, adminUpdateInquiryStatus, adminAddInquiryNote } from "../../api/business";
import AdminShell from "../layout/AdminShell";

const STATUS_COLORS = { NEW: "default", FOLLOW_UP: "warning", CONVERTED: "success", LOST: "error" };
const STATUS_OPTIONS = ["NEW", "FOLLOW_UP", "CONVERTED", "LOST"];
const SOURCE_OPTIONS = ["SERVICE", "BUSINESS", "PRODUCT"];

function InquiryDetailsModal({ id, onClose }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");

  const { data: inquiry } = useQuery({
    queryKey: ["business-inquiry", id],
    queryFn: () => adminGetInquiry(id).then((r) => r.data),
    enabled: !!id,
  });

  const statusMutation = useMutation({
    mutationFn: (status) => adminUpdateInquiryStatus(id, { status }),
    onSuccess: () => { qc.invalidateQueries(["business-inquiry", id]); qc.invalidateQueries(["admin-inquiries"]); toast.success("Status updated"); },
    onError: () => toast.error("Update failed"),
  });

  const noteMutation = useMutation({
    mutationFn: () => adminAddInquiryNote(id, { note }),
    onSuccess: () => { qc.invalidateQueries(["business-inquiry", id]); setNote(""); toast.success("Note added"); },
    onError: () => toast.error("Failed to add note"),
  });

  if (!id) return null;

  return (
    <Dialog open={!!id} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar sx={{ bgcolor: "rgba(37,99,235,0.1)", color: "#2563EB", fontWeight: 700 }}>
            {(inquiry?.full_name || "?")[0]}
          </Avatar>
          <Typography sx={{ fontWeight: 700 }}>{inquiry?.full_name}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {!inquiry ? <CircularProgress size={24} /> : (
          <>
            <Box sx={{ display: "flex", gap: 0.6, mb: 1.5, flexWrap: "wrap" }}>
              <Chip size="small" label={inquiry.status} color={STATUS_COLORS[inquiry.status] || "default"} />
              <Chip size="small" label={inquiry.inquiry_source} variant="outlined" />
            </Box>
            <Typography sx={{ fontSize: 13.5, display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Mail size={14} color="#94A3B8" /> {inquiry.email}
            </Typography>
            <Typography sx={{ fontSize: 13.5, display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Phone size={14} color="#94A3B8" /> {inquiry.phone}
            </Typography>
            {inquiry.company_name && (
              <Typography sx={{ fontSize: 13.5, display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Building2 size={14} color="#94A3B8" /> {inquiry.company_name}
              </Typography>
            )}
            <Divider sx={{ my: 1.5 }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 0.5 }}>Message</Typography>
            <Typography sx={{ fontSize: 13.5, color: "#334155" }}>{inquiry.message}</Typography>
            <Divider sx={{ my: 1.5 }} />
            <TextField
              select size="small" label="Status" fullWidth value={inquiry.status}
              onChange={(e) => statusMutation.mutate(e.target.value)}
              sx={{ mb: 2 }}
            >
              {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s.replace("_", " ")}</MenuItem>)}
            </TextField>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 0.5 }}>Notes</Typography>
            {(inquiry.notes || []).map((n, i) => (
              <Typography key={i} sx={{ fontSize: 13, color: "#334155", mb: 0.5 }}>
                • {n.note || n} {n.created_at && <span style={{ color: "#94A3B8" }}>({dayjs(n.created_at).format("DD MMM, h:mm A")})</span>}
              </Typography>
            ))}
            <TextField
              size="small" fullWidth multiline rows={2} placeholder="Add a follow-up note..."
              value={note} onChange={(e) => setNote(e.target.value)} sx={{ mt: 1 }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "text.secondary" }}>Close</Button>
        <Button
          variant="contained" disabled={!note.trim() || noteMutation.isPending}
          onClick={() => noteMutation.mutate()}
          sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#2563EB", "&:hover": { bgcolor: "#1D4ED8" } }}
        >
          Add Note
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function BusinessInquiriesPage() {
  const [filters, setFilters] = useState({ status: "", inquiry_source: "", search: "" });
  const [viewId, setViewId] = useState(null);

  const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const { data, isLoading } = useQuery({
    queryKey: ["admin-inquiries", params],
    queryFn: () => adminGetInquiries(params).then((r) => r.data),
  });

  const rows = Array.isArray(data) ? data : data?.results ?? [];

  return (
    <AdminShell title="Business Inquiries">
      <Card sx={{ mb: 2, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
        <CardContent sx={{ p: 2.5, display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            placeholder="Search name, email, phone" size="small" sx={{ minWidth: 240 }}
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search size={16} color="#94A3B8" /></InputAdornment> }}
          />
          <TextField
            select size="small" label="Status" sx={{ minWidth: 150 }}
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            <MenuItem value="">All statuses</MenuItem>
            {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s.replace("_", " ")}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Source" sx={{ minWidth: 150 }}
            value={filters.inquiry_source}
            onChange={(e) => setFilters((f) => ({ ...f, inquiry_source: e.target.value }))}
          >
            <MenuItem value="">All sources</MenuItem>
            {SOURCE_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {["Name", "Contact", "Source", "Status", "Date", ""].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12.5, color: "#475569" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}><CircularProgress size={26} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 7, color: "text.secondary" }}>
                    <Inbox size={28} style={{ opacity: 0.35, marginBottom: 8 }} />
                    <Typography fontSize={14}>No inquiries found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell sx={{ fontWeight: 600, fontSize: 13.5 }}>{row.full_name}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 13 }}>{row.email}</Typography>
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{row.phone}</Typography>
                    </TableCell>
                    <TableCell><Chip size="small" label={row.inquiry_source} variant="outlined" /></TableCell>
                    <TableCell><Chip size="small" label={row.status} color={STATUS_COLORS[row.status] || "default"} /></TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: "text.secondary" }}>{dayjs(row.created_at).format("DD MMM YYYY")}</TableCell>
                    <TableCell>
                      <Tooltip title="View details">
                        <IconButton size="small" onClick={() => setViewId(row.id)}><Eye size={16} /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Card>

      <InquiryDetailsModal id={viewId} onClose={() => setViewId(null)} />
    </AdminShell>
  );
}